// frontend/src/utils/portfolioCache.ts
// IndexedDB-based caching for large portfolio data
// Replaces localStorage to handle portfolios with 100s of items

const DB_NAME = 'SpatialRiskDB';
const DB_VERSION = 1;
const PORTFOLIO_STORE = 'portfolioCache';
const MAP_STORE = 'mapCache';

// ============================================================================
// DATABASE INITIALIZATION
// ============================================================================

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create portfolio cache store
      if (!db.objectStoreNames.contains(PORTFOLIO_STORE)) {
        const portfolioStore = db.createObjectStore(PORTFOLIO_STORE, { keyPath: 'cacheKey' });
        portfolioStore.createIndex('portfolioId', 'portfolioId', { unique: false });
        portfolioStore.createIndex('updatedAt', 'updatedAt', { unique: false });
      }
      
      // Create map cache store
      if (!db.objectStoreNames.contains(MAP_STORE)) {
        db.createObjectStore(MAP_STORE, { keyPath: 'cacheKey' });
      }
      
      console.log('IndexedDB: Database schema created/updated');
    };
    
    request.onsuccess = () => {
      console.log('IndexedDB: Database opened successfully');
      resolve(request.result);
    };
    
    request.onerror = () => {
      console.error('IndexedDB: Failed to open database', request.error);
      reject(request.error);
    };
  });
};

// ============================================================================
// PORTFOLIO CACHE OPERATIONS
// ============================================================================

/**
 * Save portfolio analysis to IndexedDB
 */
export const savePortfolioAnalysis = async (
  portfolioId: string | number, 
  data: any
): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readwrite');
      const store = tx.objectStore(PORTFOLIO_STORE);
      
      const record = {
        cacheKey: `portfolio_analysis_${portfolioId}`,
        portfolioId: String(portfolioId),
        data,
        updatedAt: new Date().toISOString(),
        itemCount: data.items?.length || 0
      };
      
      store.put(record);
      
      tx.oncomplete = () => {
        console.log(`IndexedDB: Saved portfolio ${portfolioId} (${record.itemCount} items)`);
        resolve();
      };
      tx.onerror = () => {
        console.error('IndexedDB: Failed to save portfolio', tx.error);
        reject(tx.error);
      };
    });
  } catch (error) {
    console.error('IndexedDB: Error saving portfolio analysis', error);
    throw error;
  }
};

/**
 * Get cached portfolio analysis by ID
 */
export const getPortfolioAnalysis = async (
  portfolioId: string | number
): Promise<any | null> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readonly');
      const store = tx.objectStore(PORTFOLIO_STORE);
      const request = store.get(`portfolio_analysis_${portfolioId}`);
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          console.log(`IndexedDB: Retrieved portfolio ${portfolioId} from cache`);
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => {
        console.error('IndexedDB: Failed to get portfolio', request.error);
        reject(request.error);
      };
    });
  } catch (error) {
    console.error('IndexedDB: Error getting portfolio analysis', error);
    return null;
  }
};

/**
 * Delete cached portfolio analysis
 */
export const deletePortfolioAnalysis = async (
  portfolioId: string | number
): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readwrite');
      const store = tx.objectStore(PORTFOLIO_STORE);
      store.delete(`portfolio_analysis_${portfolioId}`);
      
      tx.oncomplete = () => {
        console.log(`IndexedDB: Deleted portfolio ${portfolioId} cache`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error deleting portfolio analysis', error);
  }
};

// ============================================================================
// CURRENT ANALYSIS STATE
// ============================================================================

/**
 * Save the currently selected/analyzed portfolio
 */
export const setCurrentAnalysis = async (data: any): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readwrite');
      const store = tx.objectStore(PORTFOLIO_STORE);
      
      const record = {
        cacheKey: 'currentAnalysis',
        portfolioId: String(data.id || 'current'),
        data,
        updatedAt: new Date().toISOString(),
        itemCount: data.items?.length || 0
      };
      
      store.put(record);
      
      tx.oncomplete = () => {
        console.log(`IndexedDB: Saved current analysis (${record.itemCount} items)`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error saving current analysis', error);
  }
};

/**
 * Get the currently selected/analyzed portfolio
 */
export const getCurrentAnalysis = async (): Promise<any | null> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readonly');
      const store = tx.objectStore(PORTFOLIO_STORE);
      const request = store.get('currentAnalysis');
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          console.log(`IndexedDB: Retrieved current analysis (${result.itemCount} items)`);
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error getting current analysis', error);
    return null;
  }
};

/**
 * Clear the current analysis
 */
export const clearCurrentAnalysis = async (): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readwrite');
      const store = tx.objectStore(PORTFOLIO_STORE);
      store.delete('currentAnalysis');
      
      tx.oncomplete = () => {
        console.log('IndexedDB: Cleared current analysis');
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error clearing current analysis', error);
  }
};

// ============================================================================
// MAP DATA CACHE
// ============================================================================

/**
 * Save map data for navigation to Map page
 */
export const saveMapData = async (data: any): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MAP_STORE, 'readwrite');
      const store = tx.objectStore(MAP_STORE);
      
      const record = {
        cacheKey: 'mappedPortfolio',
        data,
        updatedAt: new Date().toISOString(),
        locationCount: data.locations?.length || 0
      };
      
      store.put(record);
      
      tx.oncomplete = () => {
        console.log(`IndexedDB: Saved map data (${record.locationCount} locations)`);
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error saving map data', error);
  }
};

/**
 * Get map data
 */
export const getMapData = async (): Promise<any | null> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MAP_STORE, 'readonly');
      const store = tx.objectStore(MAP_STORE);
      const request = store.get('mappedPortfolio');
      
      request.onsuccess = () => {
        const result = request.result;
        if (result) {
          console.log(`IndexedDB: Retrieved map data (${result.locationCount} locations)`);
          resolve(result.data);
        } else {
          resolve(null);
        }
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error getting map data', error);
    return null;
  }
};

/**
 * Clear map data
 */
export const clearMapData = async (): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(MAP_STORE, 'readwrite');
      const store = tx.objectStore(MAP_STORE);
      store.delete('mappedPortfolio');
      
      tx.oncomplete = () => {
        console.log('IndexedDB: Cleared map data');
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error clearing map data', error);
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clear all cached data
 */
export const clearAllCache = async (): Promise<void> => {
  try {
    const db = await openDB();
    
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction([PORTFOLIO_STORE, MAP_STORE], 'readwrite');
      tx.objectStore(PORTFOLIO_STORE).clear();
      tx.objectStore(MAP_STORE).clear();
      
      tx.oncomplete = () => {
        console.log('IndexedDB: Cleared all cache');
        resolve();
      };
      tx.onerror = () => reject(tx.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error clearing all cache', error);
  }
};

/**
 * Get cache statistics
 */
export const getCacheStats = async (): Promise<{
  portfolioCount: number;
  totalItems: number;
}> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(PORTFOLIO_STORE, 'readonly');
      const store = tx.objectStore(PORTFOLIO_STORE);
      const request = store.getAll();
      
      request.onsuccess = () => {
        const records = request.result || [];
        const stats = {
          portfolioCount: records.length,
          totalItems: records.reduce((sum, r) => sum + (r.itemCount || 0), 0)
        };
        resolve(stats);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error('IndexedDB: Error getting cache stats', error);
    return { portfolioCount: 0, totalItems: 0 };
  }
};

/**
 * Check if IndexedDB is supported
 */
export const isIndexedDBSupported = (): boolean => {
  return typeof indexedDB !== 'undefined';
};
